import { useState, useEffect } from 'react';
import { History, Calendar, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getRecommendationHistory, addFavorite, removeFavorite, checkFavoriteStatus } from '../utils/api';
import api from '../utils/api';
import toast from 'react-hot-toast';

interface RecommendationHistory {
  id: string;
  date: string;
  searchQuery: {
    gender?: string;
    runningExperience?: string;
    terrainType?: string;
    weeklyDistance?: string;
    budgetRange?: string;
  };
  recommendations: {
    shoeId: string;
    shoeName: string;
    brand: string;
    shoeImage: string;
    currentPrice: number;
    originalPrice?: number;
    discountPercentage?: number;
    shoeUrl: string;
    rating: number;
    tags: string[];
    rank: number;
  }[];
}

const HistoryPage = () => {
  const { isAuthenticated } = useAuth();
  const [history, setHistory] = useState<RecommendationHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  const [favoriteStatuses, setFavoriteStatuses] = useState<{[key: string]: boolean}>({});

  // Load recommendation history from API (2025-10-10: Free for all registered users)
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await getRecommendationHistory(1, 20); // Load first 20 records
        
        if (response.success && response.data) {
          // Data is already in the correct format from RecommendationHistory
          const transformedHistory = response.data.map((item: any) => ({
            id: item._id,
            date: item.createdAt,
            searchQuery: item.searchQuery || {},
            recommendations: (item.recommendations || []).map((rec: any, index: number) => ({
              shoeId: rec.shoeId || rec._id || '',
              shoeName: rec.shoeName || 'Running Shoe',
              brand: rec.brand || 'Brand',
              shoeImage: rec.shoeImage || '/placeholder-shoe.jpg',
              currentPrice: rec.currentPrice || 0,
              originalPrice: rec.originalPrice || rec.currentPrice || 0,
              discountPercentage: rec.discountPercentage || 0,
              shoeUrl: rec.shoeUrl || '#',
              rating: rec.rating || 4.5,
              tags: rec.tags || ['AI Recommended'],
              rank: rec.rank || index + 1
            }))
          }));
          
          setHistory(transformedHistory);
        } else {
          console.warn('No recommendation history found');
          setHistory([]);
        }
      } catch (error: any) {
        console.error('Error loading recommendation history:', error);
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [isAuthenticated]);

  // Load favorite statuses when history changes
  useEffect(() => {
    if (history.length > 0 && isAuthenticated) {
      const loadFavoriteStatuses = async () => {
        const statuses: {[key: string]: boolean} = {};

        for (const session of history) {
          for (const rec of session.recommendations) {
            try {
              // Generate consistent shoeId format: Brand-Model (same as EnhancedRecommendationPage)
              const modelName = rec.shoeName.startsWith(rec.brand) ? rec.shoeName : `${rec.brand} ${rec.shoeName}`;
              const shoeId = modelName.replace(/\s+/g, '-');

              const result = await checkFavoriteStatus(shoeId);
              statuses[shoeId] = result.isFavorite;
            } catch (error) {
              // Ignore errors for individual shoes
              const modelName = rec.shoeName.startsWith(rec.brand) ? rec.shoeName : `${rec.brand} ${rec.shoeName}`;
              const shoeId = modelName.replace(/\s+/g, '-');
              statuses[shoeId] = false;
            }
          }
        }

        setFavoriteStatuses(statuses);
      };

      loadFavoriteStatuses();
    }
  }, [history, isAuthenticated]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatSearchQuery = (query: any) => {
    const parts = [];
    
    // 基础信息
    if (query.gender) parts.push(query.gender);
    if (query.runningExperience) parts.push(query.runningExperience);
    
    // 跑步需求
    if (query.purpose && query.purpose !== 'daily-training') {
      parts.push(query.purpose.replace('-', ' '));
    }
    if (query.terrainType && query.terrainType !== 'road') {
      parts.push(`${query.terrainType} terrain`);
    }
    if (query.weeklyDistance) parts.push(`${query.weeklyDistance}/week`);
    
    // 个人特征
    if (query.footWidth && query.footWidth !== 'medium') {
      parts.push(`${query.footWidth} width`);
    }
    
    // 品牌偏好
    if (query.brandPreferences?.length) {
      const brands = query.brandPreferences.slice(0, 2).join(', ');
      parts.push(`prefers ${brands}`);
    }
    
    // 预算
    if (query.budgetRange) {
      parts.push(`${query.budgetRange} budget`);
    } else if (query.budget) {
      parts.push(`$${query.budget} budget`);
    }
    
    return parts.join(' • ');
  };

  // Commented out - function is not used (additionalRequirements property doesn't exist on searchQuery)
  // const formatAdditionalRequirements = (text: string) => {
  //   if (!text || text.trim() === '') return '';
  //
  //   const maxLength = 40;
  //   if (text.length <= maxLength) return text;
  //
  //   return `${text.substring(0, maxLength)}...`;
  // };

  const handleFavoriteClick = async (rec: any, gender?: string) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add favorites');
      return;
    }

    try {
      // Check if user has premium access
      const permission = await api.checkBuyNowPermission();

      if (!permission.hasPermission) {
        if (permission.needsAuth) {
          toast.error('Please sign in to add favorites');
        } else if (permission.needsPayment) {
          toast.error('Premium subscription required for favorites. Please upgrade to Premium.');
        } else {
          toast.error('Unable to access favorites feature');
        }
        return;
      }

      // Generate consistent shoeId format: Brand-Model (same as EnhancedRecommendationPage)
      const modelName = rec.shoeName.startsWith(rec.brand) ? rec.shoeName : `${rec.brand} ${rec.shoeName}`;
      const shoeId = modelName.replace(/\s+/g, '-');
      const currentlyFavorite = favoriteStatuses[shoeId] || false;

      if (currentlyFavorite) {
        // Remove from favorites
        await removeFavorite(shoeId);
        setFavoriteStatuses(prev => ({ ...prev, [shoeId]: false }));
        toast.success('Removed from favorites');
      } else {
        // Add to favorites
        // 将性别信息保存到 shoeBrand 字段（用于在收藏列表中显示）
        const genderLabel = gender === 'male' ? 'Men' :
                           gender === 'female' ? 'Women' :
                           rec.brand; // 如果没有性别信息，回退到品牌名

        await addFavorite({
          shoeId: shoeId,  // Use consistent format
          shoeName: rec.shoeName,
          shoeBrand: genderLabel,  // 保存性别而不是品牌
          shoeImage: rec.shoeImage,
          shoeUrl: rec.shoeUrl,
          currentPrice: rec.currentPrice,
          originalPrice: rec.originalPrice,
          discountPercentage: rec.discountPercentage
        });
        setFavoriteStatuses(prev => ({ ...prev, [shoeId]: true }));
        toast.success('Added to favorites');
      }
    } catch (error: any) {
      console.error('Error toggling favorite:', error);
      if (error.message && error.message.includes('Premium')) {
        toast.error('Premium subscription required for favorites');
      } else {
        toast.error(error.message || 'Failed to update favorites');
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <History className="h-16 w-16 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Sign in to view your history
          </h2>
          <p className="text-gray-600 mb-6">
            Create an account to access your recommendation history.
          </p>
          <button className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Recommendation History</h1>
          <p className="text-gray-600">
            Review your past shoe recommendations and search criteria.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
            <span className="ml-3 text-gray-600">Loading your history...</span>
          </div>
        ) : history.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <History className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              No recommendations yet
            </h2>
            <p className="text-gray-600 mb-6">
              Start by getting your first personalized shoe recommendations!
            </p>
            <button className="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors">
              Get Recommendations
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {history.map((session) => (
              <div
                key={session.id}
                className="bg-white rounded-2xl shadow-lg overflow-hidden"
              >
                {/* Session Header */}
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-orange-600" />
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {formatDate(session.date)}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {formatSearchQuery(session.searchQuery)}
                        </p>
                        {/* Commented out - additionalRequirements property doesn't exist on searchQuery type */}
                        {/* {session.searchQuery.additionalRequirements && (
                          <p className="text-xs text-gray-500 italic mt-1" title={session.searchQuery.additionalRequirements}>
                            + {formatAdditionalRequirements(session.searchQuery.additionalRequirements)}
                          </p>
                        )} */}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">
                        {session.recommendations.length} recommendations
                      </span>
                      <button
                        onClick={() => setExpandedHistory(
                          expandedHistory === session.id ? null : session.id
                        )}
                        className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        {expandedHistory === session.id ? 'Collapse' : 'View Details'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Quick Preview */}
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {session.recommendations.slice(0, 3).map((rec) => (
                      <div
                        key={rec.shoeId}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <img
                          src={rec.shoeImage}
                          alt={rec.shoeName}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            #{rec.rank} {rec.shoeName}
                          </p>
                          <p className="text-sm text-green-600 font-semibold">
                            ${rec.currentPrice}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedHistory === session.id && (
                  <div className="border-t border-gray-100 p-6">
                    <h4 className="font-semibold text-gray-900 mb-4">
                      Complete Recommendation Results
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {session.recommendations.map((rec) => {
                        // Generate consistent shoeId for favorite status lookup
                        const modelName = rec.shoeName.startsWith(rec.brand) ? rec.shoeName : `${rec.brand} ${rec.shoeName}`;
                        const shoeId = modelName.replace(/\s+/g, '-');

                        return (
                          <div
                            key={rec.shoeId}
                            className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                          >
                            <div className="relative">
                              <img
                                src={rec.shoeImage}
                                alt={rec.shoeName}
                                className="w-full h-32 object-cover"
                              />
                              <div className="absolute top-2 left-2 bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-1 rounded-full">
                                #{rec.rank}
                              </div>
                            </div>

                            <div className="p-4">
                              <h5 className="font-semibold text-gray-900 text-sm mb-1">
                                {rec.shoeName}
                              </h5>
                              <p className="text-xs text-gray-500 mb-3">{rec.brand}</p>

                              <div className="mb-3">
                                <div className="text-lg font-bold text-green-600">
                                  ${rec.currentPrice}
                                </div>
                                {rec.originalPrice && rec.originalPrice > rec.currentPrice && (
                                  <div className="text-xs text-gray-500 line-through mb-1">
                                    ${rec.originalPrice}
                                  </div>
                                )}
                                {rec.originalPrice && rec.originalPrice > rec.currentPrice && (
                                  <div className="text-sm">
                                    <span className="text-red-600 font-semibold">{rec.discountPercentage}% OFF</span>
                                    <span className="text-orange-600 font-bold ml-2">Save ${rec.originalPrice - rec.currentPrice}</span>
                                  </div>
                                )}
                              </div>

                              <div className="flex space-x-2">
                                <button
                                  onClick={() => window.open(rec.shoeUrl, '_blank')}
                                  className="flex-1 flex items-center justify-center px-3 py-2 text-xs bg-orange-600 text-white rounded font-medium hover:bg-orange-700 transition-colors"
                                >
                                  Buy Now
                                </button>
                                <button
                                  onClick={() => handleFavoriteClick(rec, session.searchQuery.gender)}
                                  className="px-3 py-2 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                                >
                                  <Heart
                                    className={`h-3 w-3 ${favoriteStatuses[shoeId] ? 'fill-red-500 text-red-500' : ''}`}
                                  />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default HistoryPage;